import React from 'react';
import { App, Button, Layout, Select, Spin, Tooltip } from 'antd';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ExportBuilderAdvancedDrawer from '../../components/body/export_builder_advanced/ExportBuilderAdvancedDrawer';
import { ApplicationState } from '../../reducers';
import {
  fetchExportBuilderAdvancedTemplates,
  fetchExportBuilderAdvancedColumns,
  fetchExportBuilderAdvancedPreview,
  fetchExportBuilderAdvancedSegment,
  fetchExportBuilderAdvancedSheets,
} from '../../actions/brand/export_builder_advanced/fetch';
import ExportBuilderAddTemplateDrawer, {
  createValidationSchemaTemplate,
} from '../../components/body/export_builder_advanced/ExportBuilderAddTemplateDrawer';
import PageFormik from '../../components/global/page/PageFormik';
import ExportBuilderAdvancedEdit from '../../components/body/export_builder_advanced/ExportBuilderAdvancedEdit';
import {
  updateExportBuilderAdvancedColumnOrder,
  updateExportBuilderAdvancedTemplate,
  updateExportBuilderAdvancedColumn,
  createExportBuilderAdvancedSheet,
  updateExportBuilderAdvancedSheet,
  deleteExportBuilderAdvancedSheet,
  copyEbpTemplate,
} from '../../actions/brand/export_builder_advanced/update';
import { AsyncDispatch } from '../../../types/global';
import {
  deleteExportBuilderAdvancedTemplate,
  deleteExportBuilderAdvancedColumn,
} from '../../actions/brand/export_builder_advanced/delete';
import { hasPermission } from '../../utils/Permissions';
import EnterprisePromotion from '../../components/global/EnterprisePromotion/EnterprisePromotion';
import { withErrorBoundary } from '../../components/global/ErrorBoundary';
import {
  ExportBuilderAdvancedColumn,
  ExportBuilderAdvancedSheet,
} from '../../../types/export_builder_advanced';
import { segmentsMappedWithOptions } from '../../selectors/export_builder_advanced/exportBuilderAdvancedSelector';
import { DEFAULT_SHEET_NAME } from '../../constants/ImportExportConstants';
import { fetchExportOptions } from '../../actions/brand/export/fetch';
import { isReceiver, isPDMUser } from '../../utils/UserUtils';
import { intercomEvent } from '../../utils/IntercomUtils';
import { getSelectedBrandCode } from '../../selectors/brand/brandSelector';
import { useAppDispatch } from '../../store/ReduxStore';
import EBPPreviewTable from '../../components/body/export_builder_advanced/EBPPreviewTable';
import { fetchUsedDynamicValueResources } from '../../actions/settings/dynamicFieldList';

const { Content } = Layout;

const ExportBuilderAdvancedContainer: React.FC = () => {
  const dispatch: AsyncDispatch = useAppDispatch();
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const {
    user,
    brandId,
    templates,
    templateColumns,
    templateSheets,
    receiver,
    segments,
    fetchingColumns,
    fetchingApplicationResources,
    fetchingSegment,
    preview,
    hasApiAccess,
    brandCode,
    usedEbpIds,
  } = useSelector((state: ApplicationState) => {
    return {
      user: state.user.user,
      brandId: state.parent.brands.selectedBrandId || undefined,
      templates: state.brand.exportBuilderAdvanced.templates,
      templateColumns: state.brand.exportBuilderAdvanced.templateColumns,
      templateSheets: state.brand.exportBuilderAdvanced.templateSheets,
      segments: segmentsMappedWithOptions(state),
      fetchingColumns: state.brand.exportBuilderAdvanced.fetchingColumns,
      receiver: state.user.user && isReceiver(state.user.user),
      fetchingApplicationResources: state.resources.fetchingApplicationResources,
      fetchingSegment: state.brand.exportBuilderAdvanced.fetchingSegment,
      preview: state.brand.exportBuilderAdvanced.preview,
      hasApiAccess: hasPermission(state.user.user, 'has_api_access'),
      brandCode: getSelectedBrandCode(state),
      usedEbpIds: state.parent.dynamicFieldList.usedEbpIds,
    };
  }, shallowEqual);

  const pdmUser = React.useMemo(() => isPDMUser(user!), [user]);

  const [drawerTemplateVisible, setDrawerTemplateVisible] = React.useState(false);
  const [drawerColumnVisible, setDrawerColumnVisible] = React.useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);

  const handleSelectedTemplateIdChange = React.useCallback(
    (id: number | null) => {
      if (selectedTemplateId !== id) {
        dispatch({ type: 'CLEAR_EXPORT_BUILDER_ADVANCED_SHEETS' });
        setActiveSheetId(null);
        setLastFetchedSheetId(null);
      }

      setSelectedTemplateId(id);
    },
    [selectedTemplateId, dispatch]
  );
  const [isNewTemplate, setIsNewTemplate] = React.useState(false);
  const [selectedColId, setSelectedColId] = React.useState<number | null>();
  const [addRightToColId, setAddRightToColId] = React.useState<number | null>();
  const [activeSheetId, setActiveSheetId] = React.useState<string | null>(null);
  const [lastFetchedSheetId, setLastFetchedSheetId] = React.useState<string | null>(null);

  const selectedTemplate = selectedTemplateId
    ? templates.find(({ id }) => id === selectedTemplateId)
    : undefined;

  const handleActiveSheetChange = React.useCallback(
    (sheetId: string | null, isUserSelection?: boolean) => {
      setActiveSheetId(sheetId);

      // Fetch preview and columns for multi-sheet templates when sheet changes
      if (
        selectedTemplateId &&
        selectedTemplate &&
        selectedTemplate.is_multi_sheet &&
        sheetId &&
        sheetId !== lastFetchedSheetId
      ) {
        dispatch(fetchExportBuilderAdvancedPreview(selectedTemplateId, brandId, Number(sheetId)));
        dispatch(fetchExportBuilderAdvancedColumns(selectedTemplateId, brandId, Number(sheetId)));
        setLastFetchedSheetId(sheetId);
      }
    },
    [selectedTemplateId, selectedTemplate, lastFetchedSheetId, dispatch, brandId]
  );

  const handleCreateSheet = (sheetName: string) => {
    return dispatch(createExportBuilderAdvancedSheet(selectedTemplateId!, sheetName));
  };

  const handleDeleteSheet = (sheetId: number) => {
    return dispatch(deleteExportBuilderAdvancedSheet(selectedTemplateId!, sheetId));
  };

  const handleRenameSheet = (sheetId: number, newName: string) => {
    return dispatch(updateExportBuilderAdvancedSheet(selectedTemplateId!, sheetId, newName));
  };

  const handleClearSheets = async () => {
    dispatch({ type: 'CLEAR_EXPORT_BUILDER_ADVANCED_SHEETS' });
    setActiveSheetId(null);
    setLastFetchedSheetId(null);
  };

  const handleToggleMultiSheet = async (
    isEnabled: boolean,
    preserveSheetName?: string,
    currentColumns?: any[]
  ) => {
    if (!selectedTemplate || !brandId) return;

    if (isEnabled) {
      // 1. Gather all existing columns from the form
      const existingColumns = currentColumns || templateColumns || [];

      // 2. Delete all existing sheets
      try {
        const sheetsResponse = await dispatch(
          fetchExportBuilderAdvancedSheets(selectedTemplate.id)
        );
        const sheets: ExportBuilderAdvancedSheet[] =
          sheetsResponse.value.data.sheets || sheetsResponse.value.data || [];

        if (sheets.length > 0) {
          await Promise.all(
            sheets.map((sheet: ExportBuilderAdvancedSheet) =>
              dispatch(
                deleteExportBuilderAdvancedSheet(
                  selectedTemplate.id,
                  typeof sheet.id === 'string' ? parseInt(sheet.id, 10) : sheet.id
                )
              )
            )
          );
        }
      } catch (error) {
        // Error handled silently
      }

      // 3. Update template to multi-sheet
      const templateUpdateObj = {
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        csv_separator: selectedTemplate.csv_separator,
        sheet_name: preserveSheetName || selectedTemplate.sheet_name || DEFAULT_SHEET_NAME,
        file_type_id: selectedTemplate.file_type_id,
        parent_owner_brand_id: brandId,
        is_multi_sheet: true,
      };

      await dispatch(updateExportBuilderAdvancedTemplate(templateUpdateObj));

      // 4. Get the auto-created sheet (backend automatically creates one)
      try {
        const sheetsResponse = await dispatch(
          fetchExportBuilderAdvancedSheets(selectedTemplate.id)
        );
        const sheets: ExportBuilderAdvancedSheet[] =
          sheetsResponse.value.data.sheets || sheetsResponse.value.data || [];

        if (sheets.length > 0) {
          const autoCreatedSheet = sheets[0];

          // 4.5. Clear any existing columns from the auto-created sheet to avoid duplicates
          try {
            await dispatch(
              fetchExportBuilderAdvancedColumns(
                selectedTemplate.id,
                brandId,
                typeof autoCreatedSheet.id === 'string'
                  ? parseInt(autoCreatedSheet.id, 10)
                  : autoCreatedSheet.id
              )
            );
            const existingSheetColumns = templateColumns || [];

            if (existingSheetColumns.length > 0) {
              const deletePromises = existingSheetColumns.map(column =>
                dispatch(deleteExportBuilderAdvancedColumn(column.id))
              );
              await Promise.all(deletePromises);
            }
          } catch (error) {
            // Error handled silently
          }

          // 5. Create new columns on the auto-created sheet based on form columns
          if (existingColumns.length > 0) {
            const columnCreatePromises = existingColumns.map((column, index) => {
              const columnData = {
                template_id: selectedTemplate.id,
                sheet_id: autoCreatedSheet.id,
                name: column.label || column.name || `Column ${index + 1}`,
                number: index + 1,
                configuration: {
                  parent_owner_brand_id: brandId,
                  segment_id: null,
                  value: column.dataKey || column.value || column.label || column.name,
                  group: 1,
                  group_separator: '<linebreak>',
                },
                code: null,
              };
              return dispatch(updateExportBuilderAdvancedColumn(columnData));
            });
            await Promise.all(columnCreatePromises);
          }

          setActiveSheetId(autoCreatedSheet.id.toString());
        }

        dispatch({ type: 'CLEAR_EXPORT_BUILDER_ADVANCED_SHEETS' });
      } catch (error) {
        // Error handled silently
      }
    } else {
      // Convert to single-sheet: just update template
      const templateUpdateObj = {
        id: selectedTemplate.id,
        name: selectedTemplate.name,
        csv_separator: selectedTemplate.csv_separator,
        sheet_name: preserveSheetName || selectedTemplate.sheet_name,
        file_type_id: selectedTemplate.file_type_id,
        parent_owner_brand_id: brandId,
        is_multi_sheet: false,
      };

      await dispatch(updateExportBuilderAdvancedTemplate(templateUpdateObj));
      dispatch({ type: 'CLEAR_EXPORT_BUILDER_ADVANCED_SHEETS' });
      setActiveSheetId(null);
      setLastFetchedSheetId(null);
    }
  };

  React.useEffect(() => {
    intercomEvent('viewed-import-export', { location: 'EBP', brand_code: brandCode! });
  }, [brandCode]);

  React.useEffect(() => {
    dispatch(fetchExportBuilderAdvancedTemplates(brandId));
    dispatch(fetchUsedDynamicValueResources());

    for (let i = 1; i <= 15; i++) {
      dispatch(fetchExportBuilderAdvancedSegment(brandId, i));
    }
  }, [brandId, dispatch, pdmUser]);

  React.useEffect(() => {
    if (selectedTemplateId && !isNewTemplate) {
      // Only fetch for single-sheet templates or initial load
      // Multi-sheet templates are handled by handleActiveSheetChange
      if (!selectedTemplate?.is_multi_sheet) {
        dispatch(fetchExportBuilderAdvancedPreview(selectedTemplateId, brandId));
        dispatch(fetchExportBuilderAdvancedColumns(selectedTemplateId, brandId));
      }
    }
  }, [dispatch, selectedTemplateId, brandId, isNewTemplate, selectedTemplate]);

  // Fetch sheets when template is converted to multi-sheet (but not for brand new templates)
  React.useEffect(() => {
    if (
      selectedTemplateId &&
      selectedTemplate &&
      selectedTemplate.is_multi_sheet &&
      templateSheets.length === 0 &&
      !isNewTemplate
    ) {
      dispatch(fetchExportBuilderAdvancedSheets(selectedTemplateId));
    }
  }, [selectedTemplate, selectedTemplateId, templateSheets.length, dispatch, isNewTemplate]);

  // Auto-select first sheet when sheets are loaded (only if user hasn't selected one)
  React.useEffect(() => {
    if (
      selectedTemplateId &&
      selectedTemplate &&
      selectedTemplate.is_multi_sheet &&
      templateSheets.length > 0 &&
      !activeSheetId
    ) {
      const firstSheet = templateSheets[0];
      const firstSheetId =
        typeof firstSheet.id === 'string' ? firstSheet.id : firstSheet.id.toString();
      handleActiveSheetChange(firstSheetId);
    }
  }, [selectedTemplateId, selectedTemplate, templateSheets, activeSheetId]);

  const handleDrawerOpen = () => {
    setDrawerColumnVisible(true);
  };

  const handleSelectNextColumn = () => {
    const index = templateColumns.findIndex(col => col.id === selectedColId);
    if (index !== -1 && index + 1 < templateColumns.length) {
      const nextColId = templateColumns[index + 1].id;
      setSelectedColId(nextColId);
    }
  };

  const handleSelectPrevColumn = () => {
    const index = selectedColId
      ? templateColumns.findIndex(col => col.id === selectedColId)
      : templateColumns.length;
    if (index > 0) {
      const prevColId = templateColumns[index - 1].id;
      setSelectedColId(prevColId);
    }
  };

  const handleDeleteTemplate = () => {
    if (selectedTemplate) {
      const isEbpInUse = usedEbpIds.includes(selectedTemplateId!);

      modal.confirm({
        title: t('exportBuilderAdvanced:deleteTemplateWarning'),
        content:
          isEbpInUse && pdmUser ? (
            <div className="flex items-center gap-2">
              <InfoCircleOutlined className="ant-info-tooltip pl-1" />
              <span>{t('exportBuilderAdvanced:ebpInUseWarning')}</span>
            </div>
          ) : undefined,
        icon: <ExclamationCircleOutlined />,
        okText: t('common:delete'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => {
          setDrawerTemplateVisible(false);
          setIsNewTemplate(false);
          return dispatch(deleteExportBuilderAdvancedTemplate(selectedTemplate.id)).then(() => {
            handleSelectedTemplateIdChange(null);
            if (receiver) dispatch(fetchExportOptions('export'));
          });
        },
      });
    }
  };

  const handleCopyTemplate = async ({
    newTitle,
    columnIds,
    brandIds,
  }: {
    newTitle: string;
    columnIds: number[];
    brandIds?: number[];
  }) => {
    const sheetId =
      selectedTemplate!.is_multi_sheet && activeSheetId ? Number(activeSheetId) : undefined;
    const copyResponse = await dispatch(
      copyEbpTemplate(
        selectedTemplate!.id,
        newTitle || selectedTemplate!.name,
        columnIds,
        brandIds,
        sheetId
      )
    );

    // Refresh templates list
    dispatch(fetchExportBuilderAdvancedTemplates(brandId));

    if (selectedTemplate!.is_multi_sheet && activeSheetId) {
      setLastFetchedSheetId(null);
      handleActiveSheetChange(activeSheetId.toString(), false);
    } else {
      // For single-sheet templates, the useEffect will handle the refetch
    }

    return copyResponse;
  };

  const getInitialValues = () => {
    return {
      templateName: selectedTemplate ? selectedTemplate.name : '',
      fileTypeId: selectedTemplate ? selectedTemplate.file_type_id : 2,
      csvSeparator: selectedTemplate ? selectedTemplate.csv_separator : '',
      sheetName: selectedTemplate?.sheet_name || '',
      multiSheetEnabled: selectedTemplate ? selectedTemplate.is_multi_sheet : false,
      templateColumns: templateColumns.map(t => ({
        id: t.id,
        label: t.name,
        number: t.number,
        dataKey: t.code,
        key: t.id,
      })),
    };
  };

  if (!hasPermission(user, 'can_create_full_template')) {
    return <EnterprisePromotion />;
  }

  return (
    <Content className="content h-full" style={{ padding: 0 }}>
      <div className="page-layout">
        <div className="page-layout__top-bar">
          <div className="page-layout__top-bar__container">
            <div className="flex items-center mr-6">
              <div className="font-medium text-gray-900 mr-2">{t('glossary:template')} :</div>
              <span>
                <Select
                  data-testid="template-selection"
                  value={selectedTemplateId ? selectedTemplateId.toString() : undefined}
                  onChange={(id: string | undefined) => {
                    handleSelectedTemplateIdChange(id ? Number(id) : null);
                  }}
                  style={{ width: 400 }}
                  options={templates.map(({ id, name }) => ({
                    key: id,
                    value: id.toString(),
                    title: name,
                    label: name,
                  }))}
                  showSearch
                  filterOption={(input, option) => {
                    if (!option) return false;
                    return (
                      option.label.toLowerCase().includes(input.toLowerCase()) ||
                      option.value.toString().includes(input)
                    );
                  }}
                  placeholder={t('exportBuilderAdvanced:searchByTemplateNameId')}
                  allowClear
                />
              </span>
            </div>
            <div className="page-layout__top-bar__actions">
              <Button
                type="primary"
                onClick={() => {
                  dispatch({ type: 'CLEAR_EXPORT_BUILDER_ADVANCED_SHEETS' });
                  setActiveSheetId(null);
                  setLastFetchedSheetId(null);
                  setDrawerTemplateVisible(true);
                  setIsNewTemplate(true);
                }}
                data-testid="add-new-template"
              >
                {t('exportBuilderAdvanced:addTemplate')}
              </Button>
            </div>
          </div>
        </div>
        <div className="page-layout__content">
          {fetchingColumns || (selectedTemplateId && !selectedTemplate) ? (
            <Spin className="spinner-center" />
          ) : (
            <PageFormik
              enableReinitialize
              contentNoSpacing
              initialValues={getInitialValues()}
              validationSchema={createValidationSchemaTemplate(t)}
              onSubmit={(values, { setSubmitting, setSubmitError, resetForm, setStatus }) => {
                setSubmitting(true);

                const templateUpdateObj = {
                  id: selectedTemplate ? selectedTemplate.id : undefined,
                  name: values.templateName,
                  csv_separator: values.csvSeparator,
                  // For multi-sheet templates, preserve current sheet name or use form value
                  sheet_name: values.multiSheetEnabled
                    ? values.sheetName || selectedTemplate?.sheet_name || DEFAULT_SHEET_NAME
                    : values.sheetName,
                  file_type_id: values.fileTypeId,
                  parent_owner_brand_id: brandId,
                  is_multi_sheet: values.multiSheetEnabled || false,
                };

                const orderUpdateList = values.templateColumns.map((c: any, index: number) => ({
                  full_export_template_column_id: c.id,
                  number: index + 1,
                }));

                Promise.all([
                  dispatch(updateExportBuilderAdvancedColumnOrder(orderUpdateList)),
                  dispatch(updateExportBuilderAdvancedTemplate(templateUpdateObj)),
                ])
                  .then(async () => {
                    if (values.multiSheetEnabled && selectedTemplate) {
                      await dispatch(fetchExportBuilderAdvancedSheets(selectedTemplate.id));
                    }

                    const columns = orderUpdateList.map(
                      (t: { full_export_template_column_id: number; number: number }) => {
                        const column = templateColumns.find(
                          col => col.id === t.full_export_template_column_id
                        );
                        return { ...column, number: t.number };
                      }
                    );

                    resetForm({
                      values: {
                        ...values,
                        templateColumns: columns.map((t: ExportBuilderAdvancedColumn) => ({
                          id: t.id,
                          label: t.name,
                          number: t.number,
                          dataKey: t.code,
                        })),
                      },
                    });

                    setStatus('SUCCESS');
                    setTimeout(() => setStatus('EDIT'), 2500);
                  })
                  .catch(() => setSubmitError());
              }}
            >
              {({ setFieldValue, values }) => {
                if (!selectedTemplateId || !selectedTemplate) return null;
                return (
                  <div className="flex flex-col h-full">
                    <ExportBuilderAdvancedEdit
                      selectedTemplateId={selectedTemplateId}
                      values={values}
                      isReceiver={receiver}
                      hasApiAccess={hasApiAccess}
                      setFieldValue={setFieldValue}
                      handleDeleteTemplate={handleDeleteTemplate}
                      handleCopyTemplate={handleCopyTemplate}
                      onActiveSheetChange={handleActiveSheetChange}
                      templateSheets={templateSheets || []}
                      onCreateSheet={handleCreateSheet}
                      onDeleteSheet={handleDeleteSheet}
                      onRenameSheet={handleRenameSheet}
                      onToggleMultiSheet={handleToggleMultiSheet}
                      activeSheetId={activeSheetId ? Number(activeSheetId) : null}
                      usedEbpIds={usedEbpIds}
                      pdmUser={pdmUser}
                    />
                    <div className="border-solid border-0 border-b border-gray-400" />
                    <div className="flex-1 p-4">
                      <EBPPreviewTable
                        templateColumns={values.templateColumns || []}
                        updateColumnsOrder={(cols: any) => {
                          setFieldValue('templateColumns', cols);
                        }}
                        setDrawerVisible={(
                          visible: boolean,
                          colId: number | null,
                          newCol?: boolean
                        ) => {
                          handleDrawerOpen();
                          if (!newCol) {
                            setSelectedColId(colId);
                            setAddRightToColId(null);
                          } else {
                            setSelectedColId(null);
                            setAddRightToColId(colId);
                          }
                        }}
                        fetching={fetchingApplicationResources}
                        preview={preview}
                        segment="exportBuilderAdvanced"
                        disabled={usedEbpIds.includes(selectedTemplateId) && !pdmUser}
                      />
                    </div>
                  </div>
                );
              }}
            </PageFormik>
          )}
        </div>

        <ExportBuilderAddTemplateDrawer
          visible={drawerTemplateVisible}
          onClose={() => {
            setDrawerTemplateVisible(false);
            setIsNewTemplate(false);
          }}
          brandId={brandId}
          selectedTemplate={!isNewTemplate ? selectedTemplate : undefined}
          setNewTemplateState={(id: number) => {
            handleSelectedTemplateIdChange(id);
            setIsNewTemplate(false);
            dispatch(fetchExportBuilderAdvancedPreview(id, brandId)); // No sheet_id for single-sheet
            dispatch(fetchExportBuilderAdvancedColumns(id, brandId));
          }}
          isNewTemplate={isNewTemplate}
          receiver={!!receiver}
        />

        {drawerColumnVisible && (
          <ExportBuilderAdvancedDrawer
            fetchingSegment={fetchingSegment}
            visible={drawerColumnVisible}
            onClose={() => {
              setDrawerColumnVisible(false);
              // Refresh preview with current sheet for multi-sheet templates
              const sheetId =
                selectedTemplate && selectedTemplate.is_multi_sheet && activeSheetId
                  ? Number(activeSheetId)
                  : undefined;
              dispatch(fetchExportBuilderAdvancedPreview(selectedTemplateId!, brandId, sheetId));
            }}
            updateColumnsOrder={(cols: any) => {
              const orderUpdateList = cols.map((c: any, index: number) => ({
                full_export_template_column_id: c.id,
                number: index + 1,
              }));
              dispatch(updateExportBuilderAdvancedColumnOrder(orderUpdateList));
            }}
            selectNextColumn={() => handleSelectNextColumn()}
            selectPrevColumn={() => handleSelectPrevColumn()}
            createNewColumn={() => setSelectedColId(null)}
            segments={segments}
            selectedColId={selectedColId!}
            addRightToColId={addRightToColId}
            templateColumns={templateColumns}
            selectedTemplateId={selectedTemplateId!}
            brandId={brandId}
            activeSheetId={activeSheetId}
          />
        )}
      </div>
    </Content>
  );
};

export default withErrorBoundary(ExportBuilderAdvancedContainer);
